import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

type Mode = "signin" | "signup" | "forgot";

// Only allow same-origin relative paths — reject protocol-relative (//host) and absolute URLs.
function safeNext(raw: string | null): string {
  if (!raw) return "/members";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/members";
  return raw;
}

export default function Auth() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = useMemo(() => safeNext(params.get("next")), [params]);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) nav(next, { replace: true });
    });
  }, [nav, next]);

  const postAuthRedirect = `${window.location.origin}${next}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: postAuthRedirect,
            data: { full_name: name },
          },
        });
        if (error) throw error;
        toast.success("Check your email to confirm your account.");
      } else if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        nav(next, { replace: true });
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast.success("Password reset email sent.");
        setMode("signin");
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const signInWithGoogle = async () => {
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: postAuthRedirect,
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      nav(next, { replace: true });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <PageMeta path="/auth" title="Sign in — Dr. Sully" description="Access your MAKECHANGE membership and purchases." />
      <SiteNav />
      <main id="main" className="container max-w-md py-16 md:py-24">
        <h1 className="font-display font-bold uppercase text-3xl mb-2">
          {mode === "signup" ? "Create account" : mode === "forgot" ? "Reset password" : "Sign in"}
        </h1>
        <p className="text-silver text-sm mb-6">
          {mode === "signup"
            ? "Create your account to access purchases and your membership."
            : mode === "forgot"
              ? "We'll email you a link to set a new password."
              : "Welcome back."}
        </p>
        {mode !== "forgot" && (
          <>
            <Button
              type="button"
              onClick={signInWithGoogle}
              disabled={busy}
              className="w-full bg-white text-navy-deep hover:bg-white/90 font-display uppercase tracking-[0.2em] text-sm py-6 mb-4 flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.5 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.9-9.8 6.9-17.4z"/><path fill="#FBBC05" d="M10.3 28.6c-1-3-1-6.2 0-9.2l-7.8-6.1C-.9 18.9-.9 29.1 2.5 34.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.3-5.7c-2 1.4-4.6 2.2-7.7 2.2-6.4 0-11.8-3.7-13.7-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 my-4 text-xs text-silver/60 uppercase tracking-widest">
              <div className="h-px bg-border flex-1" /> or <div className="h-px bg-border flex-1" />
            </div>
          </>
        )}
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <Input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="bg-navy border-border text-white" />
          )}
          <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-navy border-border text-white" />
          {mode !== "forgot" && (
            <Input type="password" required minLength={8} placeholder="Password (8+ chars)" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-navy border-border text-white" />
          )}
          <Button type="submit" disabled={busy} className="w-full bg-cta text-white font-display uppercase tracking-[0.2em] text-sm py-6 shadow-cta">
            {busy ? "…" : mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
          </Button>
        </form>
        <div className="mt-6 text-sm text-silver space-y-1">
          {mode !== "signin" && <button className="underline" onClick={() => setMode("signin")}>Have an account? Sign in</button>}
          {mode !== "signup" && <div><button className="underline" onClick={() => setMode("signup")}>Create an account</button></div>}
          {mode !== "forgot" && <div><button className="underline" onClick={() => setMode("forgot")}>Forgot password?</button></div>}
          <div><Link className="underline" to="/">← Back to home</Link></div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
